import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext'; // Import the useAuth hook

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const PersonnelDetails = ({ open, onClose, personnel }) => {
  const { currentUser, userData } = useAuth();
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [edt, setEdt] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [overtimes, setOvertimes] = useState([]);
  const [overtimesStats, setOvertimesStats] = useState({ totalHeures: 0, totalMajorees: 0, count: 0 });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && personnel && userData?.establishmentId) {
      fetchEDT();
      fetchAbsences();
      fetchOvertimes();
    }
  }, [open, personnel, userData]);

  const fetchEDT = async () => {
    setLoading(true);
    try {
      if (!personnel.schedule) {
        setEdt([]);
        return;
      }

      // Convertir l'objet schedule en tableau pour l'affichage
      const edtData = Object.entries(personnel.schedule).map(([jour, horaires]) => ({
        id: jour,
        jour: jour,
        withBreak: horaires.withBreak,
        // Horaires pour le mode avec pause
        morningStart: horaires.morning?.start || '',
        morningEnd: horaires.morning?.end || '',
        afternoonStart: horaires.afternoon?.start || '',
        afternoonEnd: horaires.afternoon?.end || '',
        // Horaires pour le mode continu
        continuousStart: horaires.continuous?.start || '',
        continuousEnd: horaires.continuous?.end || '',
        // Pour la compatibilité avec l'ancien format
        heureDebut: horaires.withBreak 
          ? horaires.morning?.start 
          : horaires.continuous?.start,
        heureFin: horaires.withBreak 
          ? horaires.afternoon?.end 
          : horaires.continuous?.end,
        service: personnel.service || 'Non défini',
        active: horaires.active || false
      }));

      // Trier les jours dans l'ordre correct
      const jourOrder = {
        'Lundi': 1,
        'Mardi': 2,
        'Mercredi': 3,
        'Jeudi': 4,
        'Vendredi': 5,
        'Samedi': 6,
        'Dimanche': 7
      };

      edtData.sort((a, b) => {
        // Si le jour n'est pas dans l'ordre défini, le placer à la fin
        const orderA = jourOrder[a.jour] !== undefined ? jourOrder[a.jour] : 999;
        const orderB = jourOrder[b.jour] !== undefined ? jourOrder[b.jour] : 999;
        return orderA - orderB;
      });
      
      setEdt(edtData);
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'EDT:', error);
      setEdt([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOvertimes = async () => {
    setLoading(true);
    try {
      const overtimesRef = collection(db, 'overtimes');
      const q = query(
        overtimesRef,
        where('personnelId', '==', personnel.id),
        where('establishmentId', '==', userData.establishmentId)
      );
      const snapshot = await getDocs(q);
      const overtimesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Trier par date (plus récentes en premier)
      overtimesList.sort((a, b) => {
        const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
        const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
        return dateB - dateA;
      });
      
      setOvertimes(overtimesList);
      
      // Calculer les statistiques
      const stats = overtimesList.reduce((acc, ot) => {
        acc.totalHeures += ot.nombreHeures || 0;
        acc.totalMajorees += (ot.nombreHeures * (1 + ot.tauxMajoration / 100)) || 0;
        acc.count += 1;
        return acc;
      }, { totalHeures: 0, totalMajorees: 0, count: 0 });
      
      setOvertimesStats(stats);
    } catch (error) {
      console.error('Erreur lors de la récupération des heures supplémentaires:', error);
      setError('Erreur lors de la récupération des heures supplémentaires');
    } finally {
      setLoading(false);
    }
  };

  const fetchAbsences = async () => {
    try {
      const absencesRef = collection(db, 'absences');
      const q = query(
        absencesRef, 
        where('personnelId', '==', personnel.id),
        where('establishmentId', '==', userData.establishmentId)
      );
      const querySnapshot = await getDocs(q);
      const absencesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAbsences(absencesData);
    } catch (error) {
      setError('Erreur lors de la récupération des absences: ' + error.message);
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      if (isNaN(d.getTime())) return '';
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(d);
    } catch (error) {
      console.error('Erreur formatage date:', error, date);
      return '';
    }
  };

  const formatTime = (time) => {
    if (!time) return '--:--';
    return time;
  };

  if (!personnel) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Détails de {personnel.firstName} {personnel.lastName}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange}>
            <Tab label="Emploi du temps" />
            <Tab label="Absences" />
            <Tab label="Heures Supplémentaires" />
          </Tabs>
        </Box>

        <TabPanel value={value} index={0}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Jour</TableCell>
                    <TableCell>Horaires</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {edt.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.jour}</TableCell>
                      <TableCell>
                        {row.active ? (
                          row.withBreak ? (
                            <>
                              <Typography variant="body2">Matin: {formatTime(row.morningStart)} - {formatTime(row.morningEnd)}</Typography>
                              <Typography variant="body2">Après-midi: {formatTime(row.afternoonStart)} - {formatTime(row.afternoonEnd)}</Typography>
                            </>
                          ) : (
                            <Typography variant="body2">{formatTime(row.continuousStart)} - {formatTime(row.continuousEnd)}</Typography>
                          )
                        ) : (
                          <Typography variant="body2">Non travaillé</Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.service}</TableCell>
                      <TableCell>
                        {row.active ? (
                          <Box sx={{ color: 'success.main', fontWeight: 'bold' }}>Actif</Box>
                        ) : (
                          <Box sx={{ color: 'text.secondary' }}>Inactif</Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {edt.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Aucun emploi du temps trouvé
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={value} index={1}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date début</TableCell>
                  <TableCell>Date fin</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {absences.map((absence) => (
                  <TableRow key={absence.id}>
                    <TableCell>{formatDate(absence.dateDebut)}</TableCell>
                    <TableCell>{formatDate(absence.dateFin)}</TableCell>
                    <TableCell>{absence.type}</TableCell>
                    <TableCell>{absence.statut}</TableCell>
                  </TableRow>
                ))}
                {absences.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Aucune absence trouvée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={value} index={2}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Statistiques des heures supplémentaires */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Heures
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {overtimesStats.totalHeures.toFixed(2)}h
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e9' }}>
                    <Typography variant="body2" color="text.secondary">
                      Heures Majorées
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {overtimesStats.totalMajorees.toFixed(2)}h
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fff3e0' }}>
                    <Typography variant="body2" color="text.secondary">
                      Enregistrements
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      {overtimesStats.count}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Tableau des heures supplémentaires */}
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Heures</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Majoration</TableCell>
                      <TableCell align="right">Heures Majorées</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overtimes.map((overtime) => (
                      <TableRow key={overtime.id}>
                        <TableCell>
                          {overtime.date?.toDate ? 
                            overtime.date.toDate().toLocaleDateString('fr-FR') : 
                            new Date(overtime.date).toLocaleDateString('fr-FR')
                          }
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {overtime.nombreHeures}h
                          </Typography>
                          {overtime.heureDebut && overtime.heureFin && (
                            <Typography variant="caption" color="text.secondary">
                              {overtime.heureDebut} - {overtime.heureFin}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={overtime.type === 'normal' ? 'Normal' : 
                                   overtime.type === 'nuit' ? 'Nuit' :
                                   overtime.type === 'dimanche' ? 'Dimanche' : 'Férié'}
                            size="small"
                            color={overtime.type === 'normal' ? 'default' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${overtime.tauxMajoration}%`}
                            size="small"
                            color="success"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {(overtime.nombreHeures * (1 + overtime.tauxMajoration / 100)).toFixed(2)}h
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {overtimes.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary">
                            Aucune heure supplémentaire enregistrée
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default PersonnelDetails;
